/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage, useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { Link, useParams } from "@packages/framework";
import {
  getAdminCommunityLandingPath,
  getCommunityMemberCount,
  useRequestCommunitiesAdminCommunities,
} from "@packages/sdk";
import * as Dialog from "@radix-ui/react-dialog";

import type { ThemeType } from "../../../../../../global/emotion";
import {
  colors,
  numericPercentages,
  radius,
  spacing,
  stroke,
  zIndices,
} from "../../../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../../lib/constants/emotionMediaQueries.constants";
import type { CSSColorStrict } from "../../../../../utils/colors/types/CSSColorStrict.types";
import { AvatarCommunity } from "../../../../_base/_core/AvatarCommunity/AvatarCommunity.emotion";
import { IconButton } from "../../../../_base/_core/IconButton/IconButton.emotion";
import { ListProfile } from "../../../../_base/_core/ListProfile/ListProfile.emotion";
import { Skeleton } from "../../../../_base/_core/Skeleton/Skeleton.emotion";
import { Text } from "../../../../_base/_core/Text/Text.emotion";
import { CheckmarkIcon } from "../../../../_base/icons/Icon/GeneralIcons";
import {
  ChevronDownIcon,
  CloseIcon,
} from "../../../../_base/icons/Icon/NavigationIcons";

const checkmarkStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
});
const communitiesStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
  paddingBottom: spacing.m,
  paddingLeft: spacing.m,
  paddingRight: spacing.m,
};
const communitySelectedStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.neutralsLowest,
});
const contentStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.onOverlayBackground,
  borderRadius: radius.l,
  bottom: spacing.s,
  [cssMediaQueryMinWidthTablet]: {
    buttom: "auto",
    left: numericPercentages[50],
    margin: spacing.m,
    maxHeight: 400,
    maxWidth: 400,
    top: numericPercentages[50],
    transform: "translate(-50%, -50%)",
  },
  display: "flex",
  flexDirection: "column",
  left: spacing.s,
  margin: spacing.none,
  maxHeight: "75%",
  maxWidth: `calc(100% - ${spacing.m})`,
  overflowY: "auto",
  position: "fixed",
  top: "auto",
  transform: "none",
  width: spacing.full,
  zIndex: zIndices.modalContentCommunitySelector,
});
const headerStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: theme.semanticColors.onOverlayBackground,
  display: "flex",
  gap: spacing.s,
  padding: spacing.m,
  position: "sticky",
  top: 0,
});
const overlayStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.higherContrastMed,
  inset: spacing.none,
  position: "fixed",
  zIndex: zIndices.overlayCommunitySelector,
});
const skeletonStyle: Interpolation = {
  margin: "0 auto",
};
const skeletonTriggerClosedStyle: Interpolation = {
  height: 24,
  width: 24,
};
const skeletonTriggerExpandedStyle: Interpolation = {
  height: 40,
};
const titleStyle: Interpolation = {
  flex: 1,
  textAlign: "center",
};
const triggerStyle: Interpolation<ThemeType> = (theme) => ({
  background: colors.transparent,
  ":hover": { background: theme.semanticColors.neutralsLowest },
  borderRadius: radius.ms,
});
const triggerExpandedStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  borderColor: theme.semanticColors.neutralsLowest,
  borderStyle: "solid",
  borderWidth: stroke.regular,
  cursor: "pointer",
  display: "flex",
  gap: spacing.ms,
  height: 40,
  outline: "none",
  padding: spacing.s,
  transition: "background-color 150ms ease-in",
  width: spacing.full,
});
const triggerClosedContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  height: 40,
  justifyContent: "center",
  width: 40,
};
const triggerIconStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.primary,
});
const triggerTextStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.primary,
  flex: 1,
  textAlign: "left",
  transition: "color 150ms ease-in",
});
const communitySelectorClosedStyle: Interpolation = {
  cursor: "pointer",
  marginHorizontal: "auto",
};

export type CommunitySelectorProps = Dialog.DialogProps & {
  triggerState?: "expanded" | "closed";
};

export const CommunitySelector = ({
  triggerState = "expanded",
  ...props
}: CommunitySelectorProps) => {
  const analytics = useAnalytics();

  const { communityId } = useParams();
  const { query: queryCommunitiesAdminCommunities } =
    useRequestCommunitiesAdminCommunities();
  const t = useTranslations();

  if (!queryCommunitiesAdminCommunities.data)
    return (
      <Skeleton
        css={[
          skeletonStyle,
          triggerState === "closed"
            ? skeletonTriggerClosedStyle
            : skeletonTriggerExpandedStyle,
        ]}
      />
    );

  const communitySelected = queryCommunitiesAdminCommunities.data.results.find(
    (community) => {
      return community.id === parseInt(communityId as string);
    },
  );

  const handleCommunityOnClick = () =>
    analytics.track({
      event: HallowAnalyticsEvent.TappedWebCommunity,
    });

  const handleTriggerOnClick = () =>
    analytics.track({
      event: HallowAnalyticsEvent.TappedWebCommunityDropdown,
    });

  return (
    <Dialog.Root {...props}>
      <Dialog.Trigger
        css={[
          triggerStyle,
          triggerState === "expanded" && triggerExpandedStyle,
        ]}
        asChild={triggerState === "closed"}
        onClick={handleTriggerOnClick}
      >
        {triggerState === "expanded" ? (
          <>
            <AvatarCommunity
              color={communitySelected?.color_hex as CSSColorStrict}
              imgSrc={communitySelected?.image_url}
              loading={!communitySelected}
              name={communitySelected?.name}
              size="xs"
            />
            <Text
              overflow="ellipsis"
              size="m"
              type="button"
              css={triggerTextStyle}
            >
              {communitySelected?.name}
            </Text>
            <ChevronDownIcon opacity={0.5} css={triggerIconStyle} />
          </>
        ) : (
          <div css={triggerClosedContainerStyle}>
            <AvatarCommunity
              color={communitySelected?.color_hex as CSSColorStrict}
              imgSrc={communitySelected?.image_url}
              loading={!communitySelected}
              name={communitySelected?.name}
              size="xs"
              css={communitySelectorClosedStyle}
            />
          </div>
        )}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay css={overlayStyle} />
        <Dialog.Content css={contentStyle}>
          <div css={headerStyle}>
            <Dialog.Title asChild>
              <Text size="m" css={titleStyle} type="title">
                <I18nMessage message="web_select_your_group" />
              </Text>
            </Dialog.Title>
            <Dialog.Close asChild>
              <IconButton
                icon={<CloseIcon />}
                size="s"
                variant="transparentPrimary"
              />
            </Dialog.Close>
          </div>
          <div css={communitiesStyle}>
            {queryCommunitiesAdminCommunities.data?.results.map((community) => (
              <ListProfile
                asChild
                avatar={
                  <AvatarCommunity
                    color={community.color_hex as CSSColorStrict}
                    imgSrc={community.image_url}
                    key="avatar"
                    name={community.name}
                    size="m"
                  />
                }
                description={community.parent_details?.name}
                icon={
                  community.id === parseInt(communityId as string) ? (
                    <CheckmarkIcon.SimpleOn css={checkmarkStyle} />
                  ) : null
                }
                key={community.id}
                metadata={`${community.simple_address_label}${community.simple_address_label && getCommunityMemberCount({ community }) >= 0 ? " | " : ""}${t(
                  "create_community_content_member_count",
                  {
                    create_community_content_member_count:
                      getCommunityMemberCount({ community }),
                  },
                )}`}
                onClick={handleCommunityOnClick}
                css={
                  community.id === parseInt(communityId as string)
                    ? communitySelectedStyle
                    : null
                }
                title={community.name}
              >
                <Link href={getAdminCommunityLandingPath({ community })} />
              </ListProfile>
            ))}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
